import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect } from "react";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { defaultJurosMultaContratoForm, JurosMultaContratoForm, JurosMultaContratoSchema } from "../../../schemas/ContratoFinanceiro_cadastro/JurosMultaContrato_form";
import { FormPercentageNegativeMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_percentage_masked_input_negativo";

/**
 * Juros e Multa Cadastro Modal 
 */

type Props = {
    open: boolean,
    saving: boolean,
    idContratoFinanceiro: number,
    percentualJuros: number,
    percentualMulta: number,

    onSubmit: (form: JurosMultaContratoForm) => void,
    onClose: () => void,
};

export const CadastroJurosMultaContratoModal = ({
    open,
    saving,
    idContratoFinanceiro,
    percentualJuros,
    percentualMulta,
    onSubmit,
    onClose,

}: Props) => {

    const hookForm = useForm<JurosMultaContratoForm>({
        mode: "onSubmit",
        reValidateMode: "onSubmit",
        defaultValues: defaultJurosMultaContratoForm,
        resolver: zodResolver(JurosMultaContratoSchema),
    });

    const handleSubmit = useCallback((form: JurosMultaContratoForm) => {
        if (!saving) {
            onSubmit(form);
        }
    }, [saving, onSubmit]);

    const handleClose = useCallback(() => {
        if (!saving) {
            onClose();
        }
    }, [saving, onClose]);


    useEffect(() => {
        hookForm.setValue('idContratoFinanceiro', idContratoFinanceiro);
        hookForm.setValue('percentualJuros', percentualJuros);
        hookForm.setValue('percentualMulta', percentualMulta);

    }, [idContratoFinanceiro, percentualJuros, percentualMulta, hookForm]);

    return (
        <Dialog
            open={open}
            slotProps={{
                paper: {
                    component: 'form',
                    onSubmit: hookForm.handleSubmit(handleSubmit),
                }
            }}
            sx={{
                "& .MuiDialog-container": {
                    "& .MuiPaper-root": {
                        width: "100%",
                        maxWidth: "55rem",
                    },
                },
            }}>

            <DialogTitle>
                Cadastrar Juros e Multa por atraso de pagamento em contas a receber
            </DialogTitle>

            <DialogContent>
                <ModalLoading loading={saving}>
                    <FormContainer title={"Informações"} arrangement="fullRow">
                        <div className="w-full bg-white p-4  rounded-2xl shadow-sm space-y-3">
                            <div>
                                <div className="flex flex-row gap-4 w-full justify-between">
                                    <label className="font-medium text-gray-700 ">Multa</label>
                                </div>
                                <div className="flex flex-row gap-4 w-full m-2">
                                    <FormPercentageNegativeMaskedInput
                                        name='percentualMulta'
                                        label='Percentual Multa'
                                        required={true}
                                        reactHookForm={hookForm} />
                                </div>
                            </div>

                            <div>
                                <div className="flex flex-row gap-4 w-full justify-between">
                                    <label className="font-medium text-gray-700 ">Percentual Juros por Mês</label>
                                </div>
                                <div className="flex flex-row gap-4 w-full m-2">
                                    <FormPercentageNegativeMaskedInput
                                        name='percentualJuros'
                                        label='Percentual Juros por Mês'
                                        required={true}
                                        reactHookForm={hookForm} />

                                </div>
                            </div>
                        </div>
                    </FormContainer>
                </ModalLoading>
            </DialogContent>

            <DialogActions>
                <div className="w-full flex items-end justify-between px-4 pb-4 gap-4">
                    <div className="flex-grow" />
                    <div className="flex flex-row gap-2">
                        <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
                            <h1
                                className="text-sm text-black dark:text-white"
                                onClick={handleClose}>
                                Cancelar
                            </h1>
                        </div>

                        <div className="w-[120px]">
                            {saving ? (
                                <PrimaryButton
                                    buttonColor="primary-green">
                                    Salvando...
                                </PrimaryButton>
                            ) : (
                                <PrimaryButton
                                    type="submit"
                                    buttonColor="primary-green">
                                    Salvar
                                </PrimaryButton>
                            )}
                        </div>
                    </div>
                </div>
            </DialogActions>
        </Dialog >

    );
}